import { ConsumableLine, ConsumablePricingContext, ConsumablePricingResult } from '../consumable-models';
import { BaseConsumableRule } from '../consumable-rule-base';

/**
 * Rule: Material Vial Pricing
 * 
 * Special handling for materials (e.g., injections, infusions) where
 * the product is sold by vial/bottle rather than by unit count.
 * 
 * For materials with presentation types like VIA (vial), INJ (injection), INF (infusion),
 * the pricing may be per vial rather than per unit.
 * Note: VAT is applied separately after pricing calculations
 */
export class RuleMaterialVialPricing extends BaseConsumableRule {
    name = 'Material Vial Pricing';
    priority = 3; // Run after base pricing rules
    
    applies(line: ConsumableLine, ctx: ConsumablePricingContext): boolean {
        // Apply to materials with vial-based presentations
        const vialPresentations = ['VIA', 'INJ', 'INF'];
        return line.codeType === 'consumable' &&
               line.type === 'material' &&
               !!line.presentationCode &&
               vialPresentations.includes(line.presentationCode);
    }
    
    apply(line: ConsumableLine, ctx: ConsumablePricingContext): ConsumablePricingResult {
        // For vials, if standardPacksize is 1, the price is per vial
        // If quantity is 1 and packsize is 1, use single exit price directly
        if (line.standardPacksize === 1 && line.quantity === 1) {
            let vialPrice = line.singleExitPrice || 0;
            
            // Apply doctor markup if not a network doctor (VAT is applied separately)
            if (!ctx.isNetworkDoctor && ctx.modifier) {
                vialPrice = this.applyMarkup(vialPrice, ctx.modifier);
            }
            
            const trace = [
                `Vial pricing: R${line.singleExitPrice} per vial`,
                ...(ctx.modifier && !ctx.isNetworkDoctor 
                    ? [`After markup (${ctx.modifier}%): R${vialPrice.toFixed(2)}`]
                    : [`Vial price: R${vialPrice.toFixed(2)}`]),
                `Total: R${vialPrice.toFixed(2)} (VAT will be applied separately)`
            ];
            
            return {
                line: { ...line },
                calculatedUnitPrice: vialPrice,
                calculatedTotalPrice: vialPrice,
                trace,
                warnings: []
            };
        }
        
        // For multiple vials, calculate per vial and multiply
        const basePricePerUnit = this.calculateBasePricePerUnit(line);
        let pricePerVial = basePricePerUnit;
        
        if (!ctx.isNetworkDoctor && ctx.modifier) {
            pricePerVial = this.applyMarkup(pricePerVial, ctx.modifier);
        }
        
        const calculatedTotalPrice = pricePerVial * line.quantity;
        
        const trace = [
            `Vial pricing: ${line.quantity} vial(s) at R${pricePerVial.toFixed(2)} each`,
            `Total: R${calculatedTotalPrice.toFixed(2)} (VAT will be applied separately)`
        ];
        
        return {
            line: { ...line },
            calculatedUnitPrice: pricePerVial,
            calculatedTotalPrice,
            trace,
            warnings: []
        };
    }
}

